import { formatCount, tt } from '@agos/shared'

/**
 * What has been done to the platform - `SAAS.md` §3.12.
 *
 * **`AuditLogPage.tsx` had no helper and no test**, and it showed neither `before` nor `after`. So it
 * recorded that something happened and not **what changed** - the one question an audit trail exists
 * to answer.
 *
 * Measured on the owner's 45 events: the two newest are the same `assign_package` on the same
 * franchise **eight seconds apart**, one with `before: null` and one with `before: {id: 4, …}`.
 * Without the diff those are two identical rows, and the `null` is itself the information - that one
 * was the first assignment and the other was a change.
 */

export interface AuditEvent {
  /** A **string** on the wire - `"45"` - so never read as a number. */
  id: string
  action: string
  /**
   * Who did it, by name - `SAAS.md §3.114`.
   *
   * **Answered for all 882 rows and read by nothing.** `SELECT *` over `platform_audit_log` carries
   * `actor_name`, the trail has it populated on every row, and this parser dropped it - so the screen
   * showed e-mail addresses where legacy shows people. On an audit log, *who* is the column.
   */
  actorName: string | null
  actorEmail: string | null
  targetType: string | null
  targetKey: string | null
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  createdAt: string | null
}

/**
 * Who to name on the row - §3.114, legacy's own `actorName ?? actorEmail ?? "system"`.
 *
 * **The fallback matters more than the preference.** The page rendered `event.actorEmail ?? ''`, so a
 * row written by a scheduled job - the billing run raises one per invoice - showed an **empty cell**
 * where the actor belongs. On an audit log, a blank in that column reads as a row nobody has looked
 * at rather than as one nobody performed by hand.
 */
export function actorLabel(event: Pick<AuditEvent, 'actorName' | 'actorEmail'>): string {
  return event.actorName ?? event.actorEmail ?? tt('systém')
}

/**
 * What an action did, in words - FB-961, §3.187.
 *
 * **The column rendered `event.action` raw**, so an operator read `update_modules`, `clear_login` and
 * `set_password`. Legacy spends one line on this - `e.action.replaceAll("_", " ")` - so AGOS was behind on a
 * line rather than on a design, and §9.502 is the class: a free-form string from the server reaching a person
 * as a key.
 *
 * **Thirteen actions, read off the Java rather than guessed.** `EveryAuditActionIsLabelledTest` derives the
 * list from every literal reaching `PlatformAuditLog` and fails naming one this map does not cover, because a
 * fourteenth added to a service is otherwise a snake_case key on screen with nothing failing.
 *
 * **The fall-through de-underscores rather than printing the key**, so an action added before the guard is
 * re-run reads as words. A fall-through that prints the raw value is what made this worth an entry.
 */
const ACTION_LABELS: Record<string, string> = {
  create: tt('Vytvorené'),
  update: tt('Upravené'),
  delete: tt('Zmazané'),
  update_modules: tt('Zmenené moduly'),
  override_modules: tt('Výnimka na moduloch'),
  assign_package: tt('Priradený balík'),
  run: tt('Spustené'),
  send: tt('Odoslané'),
  set_login: tt('Nastavené prihlásenie'),
  set_password: tt('Nastavené heslo'),
  clear_login: tt('Zrušené prihlásenie'),
  set_share: tt('Nastavený podiel'),
  remove_share: tt('Odobraný podiel'),
  /* Both found by `EveryAuditActionIsLabelledTest` on its first run, where this entry's own hand census of the
     Java had **thirteen** and the real vocabulary is fifteen - `agos-a-name-census-misses-three-ways`, and the
     whole argument for deriving the list from the server rather than keeping one by hand. */
  publish: tt('Zverejnené'),
  attach_domain: tt('Pripojená doména'),
  wireguard_provision: tt('Vydaný VPN prístup'),
  wireguard_remove: tt('Odobraný VPN prístup'),
  /* §10A.323's server-side copy of the platform's own credentials, caught by this map's own guard on the first
     whole-suite run after it shipped - which is what that test is for.

     **Deliberately not amber, by the band's own argument above.** A copy does write API keys, so the reflex is to
     call it a credential - and the amber band answers *who can sign in*, which a Resend or BulkGate key does not.
     This is the VPN pair's reasoning a second time: widening amber to anything key-shaped dilutes the one signal
     it exists for. A copy is a write of settings, so it falls through to `zinc` beside `update`. */
  copy_from: tt('Skopírované nastavenia'),
}

/**
 * The actions this map names, for the guard and the cases that walk them.
 *
 * Derived rather than written a second time: a hand-kept list beside the map is how the two come to disagree,
 * and `EveryAuditActionIsLabelledTest` compares **this** against the literals the backend writes.
 */
export const LABELLED_ACTIONS: readonly string[] = Object.keys(ACTION_LABELS)

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action.replaceAll('_', ' ')
}

/**
 * Which colour an action's badge takes - FB-961, §3.187.
 *
 * **Classified by what the action does to the data, not keyed on the string.** Legacy has a six-key colour map
 * and AGOS's server writes thirteen actions, so porting it would leave `set_password` and `remove_share`
 * looking like an ordinary `update` - which is the half that matters on a long trail, where the question is
 * *what was removed* and *who touched a credential*.
 *
 * **The amber band is this entry's judgement**: `set_password`, `set_login` and `clear_login` are what somebody
 * investigating an incident is looking for, and in legacy's map they are the grey fallback because legacy's own
 * server never wrote them.
 *
 * **The VPN pair is classified by effect rather than folded into amber**, which is a decision rather than an
 * oversight: a WireGuard peer carries a key, so the reflex is to call it a credential - but the amber band
 * answers *who can sign in*, and a peer being issued or taken away is already legible as created or removed.
 * Widening amber to anything key-shaped would dilute the one signal it exists for.
 *
 * `StatusBadge`'s own colours, because §3.96 named a state rendered as a coloured word as the real gap and its
 * ratchet then looked for a pill instead and could not see it.
 */
export type AuditTone = 'emerald' | 'red' | 'amber' | 'zinc'

const REMOVES = ['delete', 'remove_share', 'clear_login', 'wireguard_remove']
const CREATES = ['create', 'wireguard_provision']
const TOUCHES_A_CREDENTIAL = ['set_password', 'set_login', 'clear_login']

export function actionTone(action: string): AuditTone {
  /* A credential first, because `clear_login` both removes something and touches a login, and which of the two
     an auditor is scanning for is the login. */
  if (TOUCHES_A_CREDENTIAL.includes(action)) return 'amber'
  if (REMOVES.includes(action)) return 'red'
  if (CREATES.includes(action)) return 'emerald'
  return 'zinc'
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function object(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

export function auditEventsOf(payload: unknown): AuditEvent[] {
  const wrapped = (payload as { events?: unknown } | undefined)?.events
  const rows = Array.isArray(payload) ? payload : Array.isArray(wrapped) ? wrapped : []
  return rows.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const row = entry as Record<string, unknown>
    // `id` arrives as a string; a numeric id is accepted too rather than dropping the row.
    const id = text(row.id) ?? (typeof row.id === 'number' ? String(row.id) : null)
    if (id === null) return []
    return [
      {
        id,
        action: text(row.action) ?? '',
        actorName: text(row.actorName),
        actorEmail: text(row.actorEmail),
        targetType: text(row.targetType),
        targetKey: text(row.targetKey),
        before: object(row.before),
        after: object(row.after),
        createdAt: text(row.createdAt),
      },
    ]
  })
}

export interface FieldChange {
  key: string
  from: string | null
  to: string | null
}

function scalar(value: unknown): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') return value === '' ? null : value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  // An object or array inside a changed field is summarised rather than dumped: this renders in a
  // list of 45 rows, and a nested JSON blob makes the page unreadable.
  return Array.isArray(value) ? `[${value.length}]` : '{…}'
}

/**
 * What changed, as the keys that differ.
 *
 * **A diff rather than the raw objects.** `before` and `after` are whole rows - `assign_package`
 * carries `id`, `notes`, `createdAt` and more - and printing both at 45 events makes the page
 * unreadable. The keys that differ are the answer to *what changed*.
 *
 * **Every key in either object is considered**, not only those in `after`: a key that was removed is
 * a change, and a diff built by walking `after` alone reports nothing for it.
 */
export function changes(event: AuditEvent): FieldChange[] {
  const before = event.before ?? {}
  const after = event.after ?? {}
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort()
  return keys.flatMap((key) => {
    // **Compared raw, rendered through `scalar`.** Comparing the rendered form would call two
    // different nested objects equal - both render `{…}` - and a `theme` that changed would report
    // as unchanged. My own test caught that; the two jobs are separate and must stay separate.
    if (JSON.stringify(before[key] ?? null) === JSON.stringify(after[key] ?? null)) return []
    return [{ key, from: scalar(before[key]), to: scalar(after[key]) }]
  })
}

/**
 * Whether this event is the first time the target was set, rather than a change to it.
 *
 * `before: null` with an `after` says so, and it is the difference between the owner's two
 * `assign_package` rows eight seconds apart.
 */
export function isFirstSet(event: AuditEvent): boolean {
  return event.before === null && event.after !== null
}

/** What changed, in words - and it says *first set* rather than listing every key as new. */
export function changeNote(event: AuditEvent): string {
  const diff = changes(event)
  if (diff.length === 0) {
    return event.before === null && event.after === null
      ? tt('Bez zaznamenanej zmeny')
      : tt('Žiadne pole sa nezmenilo')
  }
  if (isFirstSet(event)) {
    return `${tt('Prvé nastavenie')}: ${diff.map((change) => `${change.key} = ${change.to ?? tt('nič')}`).join(', ')}`
  }
  return diff
    .map((change) => `${change.key}: ${change.from ?? tt('nič')} → ${change.to ?? tt('nič')}`)
    .join(', ')
}

/** `franchise pizza-sered`, or whichever half of it came. */
export function targetLabel(event: AuditEvent): string {
  return [event.targetType, event.targetKey].filter((part) => part !== null).join(' ')
}

/**
 * How many rows the trail holds, and how many this page was given - SAAS.md §3.48.
 *
 * **Read from the response rather than from the list.** `events.length` is the page, and a page
 * cannot tell you whether it is the whole trail; `total` is `count(*)` over the table. Null when the
 * server is an older build that does not answer it, which is what keeps the note below silent rather
 * than wrong.
 */
export function auditTotals(payload: unknown): { total: number | null; limit: number | null } {
  const row = (payload ?? {}) as Record<string, unknown>
  return { total: count(row.total), limit: count(row.limit) }
}

function count(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value)
  }
  return null
}

/**
 * *100 z 347 záznamov*, or empty when nothing is being hidden - SAAS.md §3.48.
 *
 * **Empty when the page is the whole trail**, because a note on every page is a note nobody reads -
 * and empty when the server did not say, because inventing a total would be worse than the silence
 * this exists to end.
 *
 * The audit log is the one screen where *nothing happened before this* and *we stopped showing you*
 * must not look the same. The endpoint caps any request at 200, so beyond that the missing rows
 * cannot be fetched at all; saying which are missing is the least it can do.
 */
export function truncationNote(shown: number, totals: { total: number | null }): string {
  if (totals.total === null || totals.total <= shown) return ''
  return `${formatCount(shown)} ${tt('z')} ${formatCount(totals.total)} ${tt('záznamov')}`
}
